import React from 'react';
import { connect } from 'react-redux';
import { Redirect, Route, Switch, withRouter } from 'react-router-dom';

import AdminLesson from './adminLesson.jsx';

import conceptActions from '../../actions/concepts';
import AdminMainSidebar from '../../components/admin/adminMainSidebar.jsx';
import { ConceptDetail, Concepts, FillInBlankQuestions, hashToCollection, Spinner, StaffOnlyWrapper, TitleCards, ConceptsFeedback, } from '../../../Shared';

const ActivityHealth = React.lazy(() => import('../activityHealth/activityHealth'));
const ConceptFeedback = React.lazy(() => import('../feedback/conceptFeedback.jsx'));
const FillInBlankQuestion = React.lazy(() => import('../fillInBlank/fillInBlankQuestion.jsx'));
const GenAi = React.lazy(() => import('../genAi/genAi'));
const Lesson = React.lazy(() => import('../lessons/lesson.jsx'));
const Lessons = React.lazy(() => import('../lessons/lessons.jsx'));
const Question = React.lazy(() => import('../questions/question'));
const Questions = React.lazy(() => import('../questions/questions.jsx'));
const ShowTitleCard = React.lazy(() => import('../titleCards/showTitleCard.tsx'));
const TitleCardForm = React.lazy(() => import('../titleCards/titleCardForm.tsx'));
const connectAdminSidebarStorageKey = 'connect-admin-sidebar-open';
const sidebarCloseIconSrc = 'https://assets.quill.org/images/icons/s/sidebar-close.svg';
const sidebarOpenIconSrc = 'https://assets.quill.org/images/icons/s/sidebar-open.svg';

function storedConnectAdminSidebarOpen() {
  return window.localStorage.getItem(connectAdminSidebarStorageKey) !== 'false';
}

class adminContainer extends React.Component {
  state = {
    isSidebarOpen: storedConnectAdminSidebarOpen()
  }

  isGenAiRoute = () => this.props.location.pathname.startsWith('/admin/gen-ai')

  toggleSidebar = () => {
    this.setState(
      ({ isSidebarOpen }) => ({ isSidebarOpen: !isSidebarOpen }),
      () => window.localStorage.setItem(connectAdminSidebarStorageKey, String(this.state.isSidebarOpen))
    );
  }

  renderSidebarToggle = () => {
    if (!this.isGenAiRoute()) { return null }

    const { isSidebarOpen } = this.state;
    const iconSrc = isSidebarOpen ? sidebarCloseIconSrc : sidebarOpenIconSrc;

    return (
      <div className="connect-admin-sidebar-toggle-container">
        <button
          aria-expanded={isSidebarOpen}
          aria-label={isSidebarOpen ? 'Close navigation' : 'Open navigation'}
          className="focus-on-light connect-admin-sidebar-toggle"
          onClick={this.toggleSidebar}
          type="button"
        >
          <img alt="" src={iconSrc} />
        </button>
      </div>
    );
  }

  render() {
    const { concepts, dispatch, fillInBlank, questions, titleCards, conceptsFeedback, } = this.props

    const sidebarCollapsed = this.isGenAiRoute() && !this.state.isSidebarOpen;
    const content = (
      <div className={`main-admin-container${sidebarCollapsed ? ' connect-admin-sidebar-collapsed' : ''}`}>
        {this.renderSidebarToggle()}
        <Switch>
          <Route component={AdminLesson} path='/admin/lesson-view' />
          <Route component={AdminMainSidebar} path='/admin' />
        </Switch>
        <React.Suspense fallback={<Spinner />}>
          <Switch>
            <Redirect component={Lessons} exact from='/admin' to='/admin/lessons' />
            <Route component={ConceptFeedback} path='/admin/concepts-feedback/:conceptFeedbackID' />
            <Route
              path="/admin/concepts-feedback"
              render={() => (
                <ConceptsFeedback
                  concepts={concepts}
                  conceptsFeedback={conceptsFeedback}
                />
              )}
            />
            <Route
              path='/admin/concepts/:conceptID'
              render={({ match }) => (
                <ConceptDetail
                  concepts={concepts}
                  containerClassName="admin-container"
                  fillInBlank={fillInBlank}
                  includeDeleteButton={false}
                  includeQuestionForm={false}
                  match={match}
                  questions={questions}
                  renderLoading={() => <Spinner />}
                  returnLink="admin/concepts"
                />
              )}
            />
            <Route
              path='/admin/concepts'
              render={() => (
                <Concepts
                  concepts={concepts}
                  containerClassName="container"
                  excludeResponses={true}
                  includeNewConceptForm={true}
                  onSubmitNewConcept={(conceptName) => {
                    const newConcept = { name: conceptName };
                    dispatch(conceptActions.submitNewConcept(newConcept));
                  }}
                  renderAsLinks={true}
                />
              )}
            />
            <Route component={Lesson} path='/admin/lessons/:lessonID' />
            <Route component={Lessons} path='/admin/lessons' />
            <Route component={Question} path='/admin/questions/:questionID' />
            <Route component={Questions} path='/admin/questions' />
            <Route component={TitleCardForm} path='/admin/title-cards/:titleCardID/edit' />
            <Route component={TitleCardForm} path='/admin/title-cards/new' />
            <Route component={ShowTitleCard} path='/admin/title-cards/:titleCardID' />
            <Route path='/admin/title-cards' render={() => <TitleCards titleCards={titleCards} />} />
            <Route component={ActivityHealth} path='/admin/activity-health' />
            <Route component={GenAi} path='/admin/gen-ai' />
            <Route component={FillInBlankQuestion} path='/admin/fill-in-the-blanks/:questionID' />
            <Route
              path='/admin/fill-in-the-blanks'
              render={() => (
                <FillInBlankQuestions
                  basePath="fill-in-the-blanks"
                  questions={hashToCollection(fillInBlank?.data || {}) || []}
                  showCreateButton={false}
                  sortQuestions={false}
                />
              )}
            />
          </Switch>
        </React.Suspense>
      </div>
    );

    return <StaffOnlyWrapper>{content}</StaffOnlyWrapper>;
  }
}

function select(state) {
  return {
    concepts: state.concepts,
    conceptsFeedback: state.conceptsFeedback,
    fillInBlank: state.fillInBlank,
    questions: state.questions,
    titleCards: state.titleCards
  };
}

export default withRouter(connect(select, dispatch => ({dispatch}))(adminContainer));
